import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { TOTAL_PHASES, AppRow } from '../types';
import { deleteProject, getProject, importProject, importProjectFromRepo } from '../lib/api';
import { listRepoProfiles, type RepoProfile } from '../lib/auth';
import { toAppArchive, parseAppArchive, appFileName, downloadBlob } from '../lib/appIo';
import { I, StatusDot, Progress } from '../components/Shared';
import { Shell } from '../components/Shell';
import { FullPromptHistoryModal } from '../components/FullPromptHistoryModal';
import { ShareModal } from '../components/ShareModal';
import { ActivityModal } from '../components/ActivityModal';
import { isAdminRole } from '../lib/auth';

export function MyApps() {
  const navigate = useNavigate();
  const apps = useStore((s) => s.apps);
  const refreshApps = useStore((s) => s.refreshApps);
  const currentUser = useStore((s) => s.currentUser);
  // Workspace admins (and super-admins) see every member's apps in the
  // listing, so we surface the owner. Sandboxed users only see their own
  // apps and would just see their own name repeated — column hidden for them.
  const showOwner = !!currentUser && isAdminRole(currentUser.role);

  const [search, setSearch] = useState('');
  const [promptHistoryFor, setPromptHistoryFor] = useState<AppRow | null>(null);
  const [shareFor, setShareFor] = useState<AppRow | null>(null);
  const [activityFor, setActivityFor] = useState<AppRow | null>(null);
  const [importing, setImporting] = useState(false);
  const [gitImportOpen, setGitImportOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Skip server refresh if demo apps are loaded (they have no id)
  const isDemoMode = apps.length > 0 && apps.some((a) => !a.id);
  useEffect(() => {
    if (!isDemoMode) void refreshApps();
  }, [refreshApps, isDemoMode]);

  const open = (row: AppRow): void => {
    if (row.id) navigate(`/pipeline/${row.id}`);
    else navigate('/pipeline/demo');
  };

  const remove = async (row: AppRow): Promise<void> => {
    if (!row.id) return;
    if (!window.confirm(`Delete ${row.name}?`)) return;
    await deleteProject(row.id);
    await refreshApps();
  };

  const showPromptHistory = (row: AppRow): void => {
    if (!row.id) return;
    setPromptHistoryFor(row);
  };

  const exportApp = async (row: AppRow): Promise<void> => {
    if (!row.id) return;
    try {
      const { project } = await getProject(row.id);
      const blob = await toAppArchive(project);
      downloadBlob(appFileName(project.appName), blob);
    } catch (err: any) {
      window.alert(`Could not export app: ${err?.message ?? 'unknown error'}`);
    }
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setImporting(true);
    try {
      const spec = await parseAppArchive(file);
      const project = await importProject(spec);
      await refreshApps();
      navigate(`/pipeline/${project.id}`);
    } catch (err: any) {
      window.alert(`Could not import app: ${err?.message ?? 'unknown error'}`);
    } finally {
      setImporting(false);
    }
  };

  const filtered = search.trim()
    ? apps.filter((a) => a.name.toLowerCase().includes(search.toLowerCase()))
    : apps;

  const activeCount = apps.filter((a) => a.phaseApp === 'inprogress').length;
  const doneCount = apps.filter((a) => a.phaseApp === 'success').length;

  return (
    <Shell active="apps">
      <main
        style={{ padding: "18px 28px 80px", maxWidth: 1200, margin: "0 auto" }}
      >
        {/* header row */}
        <div
          style={{ display: "flex", alignItems: "flex-end", marginBottom: 22 }}
        >
          <div>
            <div className="t-micro" style={{ marginBottom: 6 }}>
              FLOOR · MY APPS
            </div>
            <h1 className="t-h1" style={{ margin: 0 }}>
              Runs in progress &amp; completed
            </h1>
          </div>
          <span style={{ flex: 1 }} />
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ position: "relative" }}>
              <span
                style={{
                  position: "absolute",
                  left: 9,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--ink-faint)",
                }}
              >
                {I.search}
              </span>
              <input
                className="input"
                placeholder="Filter runs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ width: 240, paddingLeft: 30, height: 30 }}
              />
            </div>
            <button className="btn btn--sm" onClick={() => void refreshApps()}>
              {I.refresh} Refresh
            </button>
            <button
              className="btn btn--sm"
              disabled={importing}
              onClick={() => fileInputRef.current?.click()}
              title="Import an app from an exported .app.zip"
            >
              {importing ? "Importing..." : "Import app"}
            </button>
            <button
              className="btn btn--sm"
              onClick={() => setGitImportOpen(true)}
              title="Import an app from a GitHub / Bitbucket repository"
            >
              {I.git} Import from Git
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip,application/zip"
              style={{ display: "none" }}
              onChange={(e) => void handleImportFile(e)}
            />
            <button
              className="btn btn--accent btn--sm"
              data-demo-new-app
              onClick={() => navigate("/")}
            >
              {I.plus} New app
            </button>
          </div>
        </div>

        {/* stats strip */}
        <div
          className="card"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            marginBottom: 22,
          }}
        >
          {[
            {
              label: "Active runs",
              value: String(activeCount),
              note: `of ${apps.length} total`,
            },
            {
              label: "Completed",
              value: String(doneCount),
              note: "this month",
            },
            { label: "Avg. completion", value: "--", note: "per app" },
            { label: "Pass rate", value: "--", note: "last 14 runs" },
          ].map((s, i) => (
            <div
              key={s.label}
              style={{
                padding: 18,
                borderRight: i < 3 ? "1px solid var(--line)" : "none",
              }}
            >
              <div className="t-micro" style={{ marginBottom: 8 }}>
                {s.label}
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span
                  className="t-num"
                  style={{
                    fontSize: 26,
                    fontWeight: 600,
                    letterSpacing: "-0.02em",
                  }}
                >
                  {s.value}
                </span>
                <span className="t-small muted">{s.note}</span>
              </div>
            </div>
          ))}
        </div>

        {/* table */}
        {filtered.length === 0 ? (
          <div className="card" style={{ padding: 48, textAlign: "center" }}>
            <span className="muted">No apps yet. </span>
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => navigate("/")}
            >
              Create one
            </button>
          </div>
        ) : (
          <div className="card" style={{ overflow: "hidden" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: showOwner
                  ? "2.4fr 1.2fr 1.5fr 1.3fr 130px 150px"
                  : "2.4fr 1.5fr 1.3fr 130px 150px",
                padding: "12px 18px",
                background: "var(--surface-2)",
                borderBottom: "1px solid var(--line)",
                alignItems: "center",
                gap: 12,
              }}
            >
              {(showOwner
                ? ["Name", "Created by", "Assembly Line", "Progress", "Updated", ""]
                : ["Name", "Assembly Line", "Progress", "Updated", ""]
              ).map((h, i) => (
                <span key={i} className="t-micro">
                  {h}
                </span>
              ))}
            </div>
            {filtered.map((app, i) => (
              <Row
                key={app.id ?? app.name}
                app={app}
                isLast={i === filtered.length - 1}
                showOwner={showOwner}
                onOpen={() => open(app)}
                onDelete={() => void remove(app)}
                onExport={() => void exportApp(app)}
                onPromptHistory={() => showPromptHistory(app)}
                onShare={() => setShareFor(app)}
                onActivity={() => setActivityFor(app)}
              />
            ))}
          </div>
        )}

        {promptHistoryFor && promptHistoryFor.id && (
          <FullPromptHistoryModal
            projectId={promptHistoryFor.id}
            appName={promptHistoryFor.name}
            onClose={() => setPromptHistoryFor(null)}
          />
        )}

        {shareFor && shareFor.id && (
          <ShareModal
            projectId={shareFor.id}
            appName={shareFor.name}
            canManage={
              shareFor.role === "owner" ||
              (!!currentUser && isAdminRole(currentUser.role))
            }
            onClose={() => setShareFor(null)}
            onChanged={() => void refreshApps()}
          />
        )}

        {activityFor && activityFor.id && (
          <ActivityModal
            projectId={activityFor.id}
            appName={activityFor.name}
            onClose={() => setActivityFor(null)}
          />
        )}

        {shareFor && shareFor.id && (
          <ShareModal
            projectId={shareFor.id}
            appName={shareFor.name}
            canManage={
              shareFor.role === "owner" ||
              (!!currentUser && isAdminRole(currentUser.role))
            }
            onClose={() => setShareFor(null)}
            onChanged={() => void refreshApps()}
          />
        )}

        {activityFor && activityFor.id && (
          <ActivityModal
            projectId={activityFor.id}
            appName={activityFor.name}
            onClose={() => setActivityFor(null)}
          />
        )}
        {gitImportOpen && (
          <GitImportModal
            onClose={() => setGitImportOpen(false)}
            onImported={async (projectId) => {
              setGitImportOpen(false);
              await refreshApps();
              navigate(`/pipeline/${projectId}`);
            }}
          />
        )}
        {/* belt */}
        <div className="perf-edge" style={{ marginTop: 28 }} />
        <div
          style={{
            display: "flex",
            gap: 18,
            marginTop: 14,
            alignItems: "center",
          }}
        >
          <span className="t-micro muted">
            FLOOR 01 · {activeCount} ACTIVE · {doneCount} CLEARED
          </span>
          <span style={{ flex: 1 }} />
          <span className="t-micro muted">
            SHIFT ·{" "}
            {new Date()
              .toLocaleDateString("en-US", {
                weekday: "short",
                day: "numeric",
                month: "short",
              })
              .toUpperCase()}
          </span>
        </div>
      </main>
    </Shell>
  );
}

function Row({
  app,
  isLast,
  showOwner,
  onOpen,
  onDelete,
  onExport,
  onPromptHistory,
  onShare,
  onActivity,
}: {
  app: AppRow;
  isLast: boolean;
  showOwner: boolean;
  onOpen: () => void;
  onDelete: () => void;
  onExport: () => void;
  onPromptHistory: () => void;
  onShare: () => void;
  onActivity: () => void;
}) {
  const isOwner = app.role === 'owner';
  const statusMap: Record<string, string> = {
    success: 'done',
    inprogress: 'in-progress',
    pending: 'pending',
  };
  const status = statusMap[app.phaseApp] || 'pending';
  // Per-project phase count; fall back to the legacy default for older rows.
  const appTotal = app.total > 0 ? app.total : TOTAL_PHASES;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: showOwner
          ? '2.4fr 1.2fr 1.5fr 1.3fr 130px 150px'
          : '2.4fr 1.5fr 1.3fr 130px 150px',
        padding: '16px 18px',
        borderBottom: isLast ? 'none' : '1px solid var(--line)',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <StatusDot status={status} />
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); onOpen(); }}
          style={{ color: 'var(--ink)', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {app.name}
        </a>
        {app.isShared && (
          <span
            className="chip"
            title={app.ownerLabel ? `Shared by ${app.ownerLabel} · ${app.role} access` : `Shared · ${app.role} access`}
            style={{ fontSize: 10, flexShrink: 0, textTransform: 'capitalize' }}
          >
            Shared · {app.role}
          </span>
        )}
      </div>
      {showOwner && (
        <span className="t-small muted" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {app.ownerLabel ?? '—'}
        </span>
      )}
      <span className="t-small muted" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{app.assemblyLineName ?? '-'}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Progress total={appTotal} done={app.done} now={app.done < appTotal ? app.done : -1} />
        <span className="t-num t-small muted">{app.done}/{appTotal}</span>
      </div>
      <span className="t-small muted t-num">{app.date}</span>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
        <button className="btn btn--ghost btn--sm" onClick={onOpen} title="Open app">{I.open}</button>
        {app.id && (
          <button className="btn btn--ghost btn--sm" onClick={onExport} title="Export app as a .zip of real files">{I.download}</button>
        )}
        {app.id && (
          <button
            className="btn btn--ghost btn--sm"
            onClick={onPromptHistory}
            title="View prompt history"
          >
            {I.doc}
          </button>
        )}
        {app.id && (
          <button className="btn btn--ghost btn--sm" onClick={onActivity} title="Activity — who did what">{I.users}</button>
        )}
        {app.id && isOwner && (
          <button className="btn btn--ghost btn--sm" onClick={onShare} title="Share with workspace members">{I.share}</button>
        )}
        {app.id && isOwner && (
          <button className="btn btn--ghost btn--sm" onClick={onDelete} title="Delete app">{I.trash}</button>
        )}
      </div>
    </div>
  );
}

/**
 * Import-from-Git modal: choose a saved git connection, enter the repo (and
 * optional branch), and create a NEW app-factory project from that repo. If the
 * repo carries an app.manifest.json it's reconstructed faithfully; otherwise its
 * files are imported as artifacts of an "Imported" step.
 */
function GitImportModal({
  onClose,
  onImported,
}: {
  onClose: () => void;
  onImported: (projectId: string) => void | Promise<void>;
}) {
  const [profiles, setProfiles] = useState<RepoProfile[]>([]);
  const [profileId, setProfileId] = useState<string>('');
  const [repo, setRepo] = useState('');
  const [branch, setBranch] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    listRepoProfiles()
      .then((ps) => {
        if (!alive) return;
        setProfiles(ps);
        const def = ps.find((p) => p.isDefault) ?? ps[0];
        if (def) {
          setProfileId(def.id);
          setRepo(def.repo || '');
          setBranch(def.syncBranch || def.defaultBranch || '');
        }
      })
      .catch(() => { if (alive) setProfiles([]); });
    return () => { alive = false; };
  }, []);

  const onPickProfile = (id: string): void => {
    setProfileId(id);
    const p = profiles.find((x) => x.id === id);
    if (p) { setRepo(p.repo || ''); setBranch(p.syncBranch || p.defaultBranch || ''); }
  };

  const submit = async (): Promise<void> => {
    if (!repo.trim()) { setErr('Enter the repository name to import.'); return; }
    setBusy(true); setErr(null);
    try {
      const r = await importProjectFromRepo({ profileId: profileId || undefined, repo: repo.trim(), branch: branch.trim() || undefined });
      await onImported(r.projectId);
    } catch (e: any) {
      setErr(e?.message ?? 'Import failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: 440, maxWidth: '92vw', background: 'var(--surface, #fff)', borderRadius: 12, border: '1px solid var(--line, #e2e2e2)', padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
      >
        <div style={{ fontWeight: 600, fontSize: 16 }}>Import from Git</div>
        {profiles.length === 0 ? (
          <div className="t-micro muted">
            No git connections yet. Add one in Admin → API Credentials first.
          </div>
        ) : (
          <>
            <label className="t-micro muted" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              Connection
              <select className="select select--sm" value={profileId} onChange={(e) => onPickProfile(e.target.value)} disabled={busy}>
                {profiles.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({p.host}){p.isDefault ? ' ★' : ''}</option>
                ))}
              </select>
            </label>
            <label className="t-micro muted" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              Repository
              <input className="input" value={repo} onChange={(e) => setRepo(e.target.value)} placeholder="my-app" disabled={busy} />
            </label>
            <label className="t-micro muted" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              Branch <span style={{ opacity: 0.6 }}>(optional)</span>
              <input className="input" value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="main" disabled={busy} />
            </label>
          </>
        )}
        {err && (
          <div className="t-micro" style={{ color: 'var(--err, #c0362c)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 120, overflowY: 'auto' }}>
            {err}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <button className="btn btn--sm" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn--sm btn--primary" onClick={() => void submit()} disabled={busy || profiles.length === 0}>
            {busy ? 'Importing…' : 'Import'}
          </button>
        </div>
      </div>
    </div>
  );
}
